import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Clickable, LoadingBalls, Stack, useBreakpoint } from '@moda/om';
import { useSwipeable } from 'react-swipeable';
import { useUrlParams } from '../../../hooks/useUrlParams';
import { LookCellFragment } from '../../../generated/types';
import { LooksCarouselItemHeaderNavigation } from '../LooksCarouselItemHeaderNavigation';
import { LooksCarouselMobileModal } from '../LooksCarouselMobileModal';
import { LooksClothesHanger } from '../LooksClothesHanger';
import { useLook } from '../useLook';
import { LooksCarousel } from '../LooksCarousel';
import { ResponsiveImage } from '../../ResponsiveImage';
import { useTrackLookEvent } from '../../LookTracking';

import './LooksMobileModule.scss';

interface Props {
  looks: LookCellFragment[];
}

const SM_SIZES = {
  maxWidth: 700,
  maxHeight: 1200
};

const XS_SIZES = {
  maxWidth: 400,
  maxHeight: 685
};

export const LooksMobileModule: React.FC<Props> = ({ looks }) => {
  const {
    queryParams: { look: selectedLookId },
    addParams,
    setParams,
    params
  } = useUrlParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [showVariantsCta, setShowVariantsCta] = useState(false);
  const [hasAutoSelectedFirstLook, setHasAutoSelectedFirstLook] = useState(false);

  const selectedLook = looks.find(look => look.id === selectedLookId);
  const { numberOfVariants, loading, nextLookId, prevLookId } = useLook(selectedLookId);

  const trackLookEvent = useTrackLookEvent();

  const swipeToLook = useCallback(
    (id: number | null | undefined, direction: 'next' | 'previous') => {
      if (!id) return;

      trackLookEvent('Next Look Clicked', {
        look_id: id.toString(),
        from_look_id: typeof selectedLookId === 'string' ? selectedLookId : null,
        direction,
        method: 'swipe'
      });
      addParams({ look: id.toString() });
    },
    [addParams, selectedLookId, trackLookEvent]
  );

  const { ref } = useSwipeable({
    onSwipedLeft: () => swipeToLook(nextLookId, 'next'),
    onSwipedRight: () => swipeToLook(prevLookId, 'previous')
  });

  const handleOpenProducts = useCallback(() => {
    trackLookEvent('Shop By Look Clicked', { type: 'hanger' });
    setModalOpen(true);
  }, [trackLookEvent]);

  const handleCloseProducts = useCallback(() => {
    trackLookEvent('Shop By Look Module Closed');
    setModalOpen(false);
  }, [trackLookEvent]);

  const handleSelectLook = useCallback(
    (id: string) => {
      if (id !== selectedLookId) {
        trackLookEvent('Shop By Look Clicked', { look_id: id, type: 'image' });
      }

      addParams({ look: id });
    },
    [addParams, selectedLookId, trackLookEvent]
  );

  const { matches: isExtraSmallScreen } = useBreakpoint({ lt: 'xs' });

  const { maxWidth, maxHeight } = isExtraSmallScreen ? XS_SIZES : SM_SIZES;

  useEffect(() => {
    if (!selectedLookId && !hasAutoSelectedFirstLook) {
      setParams({ ...params, look: looks[0].id }, { replace: true });
      setHasAutoSelectedFirstLook(true);
    }
  }, [setParams, params, looks, selectedLookId, hasAutoSelectedFirstLook]);

  if (!selectedLook) return null;

  return (
    <>
      <Stack className="LooksMobileModule" space={2}>
        <LooksCarouselItemHeaderNavigation className="LooksMobileModule__navigation" />

        {selectedLook && selectedLook.primaryImageUrls && selectedLook.primaryImageUrls.src && (
          <div className="LooksMobileModule__image-wrapper" ref={ref}>
            <ResponsiveImage
              className="LooksMobileModule__main-image"
              src={selectedLook.primaryImageUrls.src}
              onLoad={() => setShowVariantsCta(true)}
              maxWidth={maxWidth}
              maxHeight={maxHeight}
              alt={selectedLook.name}
            />

            {showVariantsCta && (
              <Clickable
                className="LooksMobileModule__modal-triggerer"
                onClick={handleOpenProducts}
                aria-label="Show products"
              >
                {loading && <LoadingBalls />}

                {!loading && <LooksClothesHanger>{numberOfVariants}</LooksClothesHanger>}
              </Clickable>
            )}
          </div>
        )}

        <LooksCarousel>
          {looks.map(look => (
            <Clickable
              className={classNames('LooksMobileModule__image-content', {
                'LooksMobileModule__image-content--selected': look.id === selectedLook.id
              })}
              key={look.id}
              onClick={() => handleSelectLook(look.id)}
              aria-label={look.name}
            >
              <ResponsiveImage
                className="LooksMobileModule__look-image"
                src={look?.primaryImageUrls?.src || ''}
                maxHeight={128}
                maxWidth={80}
                alt={look?.name || 'Look image'}
              />
            </Clickable>
          ))}
        </LooksCarousel>
      </Stack>

      {modalOpen && <LooksCarouselMobileModal onClose={handleCloseProducts} />}
    </>
  );
};
